'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useIsClient } from 'usehooks-ts';
import DeviceIcons from '@/components/DeviceIcons';
import OptimizedImage from '@/components/OptimizedImage';
import PremiumBadge from '@/components/PremiumBadge';
import ViverseCard from '@/components/ViverseCard';
import { WORLD_CARD_ADVERTISED_SRC_WIDTH } from '@/constants/card';
import { useCardPrefetchRef } from '@/hooks/useCardPrefetchRef';
import { Link } from '@/i18n/navigation';
import { extractRoomIdFromUrl } from '@/lib/cms';
import { formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';
import roomDefaultImage from '@/public/images/room-default.png';
import { DeviceTypeWithAndroidIOS } from '@/types/configs';

// Carries the ~236KB gz BlendVision player; load it on hover/dwell only so it
// stays out of the eager card bundle (matches ViverseWorldCardMedia).
const HoverVideoPreview = dynamic(() => import('@/components/HoverVideoPreview'), {
  ssr: false
});
type ActionWorldCardImageProps = {
  href?: string;
  externalLink?: string;
  hubsUrl?: string;
  image?: string;
  isLive?: boolean;
  imageClassName?: string;
  preferredDevices?: DeviceTypeWithAndroidIOS[];
  showPremiumTag?: boolean;
  vodDuration?: number;
  vodId?: string;
  isCardHovered?: boolean;
  isDwellPlaying?: boolean;
  title?: string;
  ownerDisplayName?: string;
};
export default function ActionWorldCardImage(t0) {
  const $ = _c(45);
  const {
    href,
    externalLink,
    hubsUrl,
    image,
    isLive,
    imageClassName,
    preferredDevices,
    showPremiumTag,
    vodDuration,
    vodId,
    isCardHovered,
    isDwellPlaying,
    title,
    ownerDisplayName
  } = t0;
  const isClient = useIsClient();
  const t = useTranslations();
  let t1;
  if ($[0] !== ownerDisplayName || $[1] !== t || $[2] !== title) {
    t1 = title ? ownerDisplayName ? t("action-world-card-image.alt-with-owner", {
      title,
      owner: ownerDisplayName
    }) : t("action-world-card-image.alt", {
      title
    }) : "";
    $[0] = ownerDisplayName;
    $[1] = t;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const imageAlt = t1;
  const t2 = href || externalLink || hubsUrl || "/";
  let t3;
  if ($[4] !== t2) {
    t3 = extractRoomIdFromUrl(t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const targetUrl = t3;
  let t4;
  if ($[6] !== vodId) {
    t4 = () => {
      if (!vodId) {
        return;
      }
      fetch(`/api/hover-video/${vodId}`).catch(console.debug);
    };
    $[6] = vodId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const cardRef = useCardPrefetchRef(t4);
  let t5;
  if ($[8] !== showPremiumTag) {
    t5 = showPremiumTag && <div className="z-raised absolute top-1.5 right-2"><PremiumBadge showLabel={true} /></div>;
    $[8] = showPremiumTag;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isClient || $[11] !== isLive || $[12] !== targetUrl) {
    t6 = isLive && isClient && <div className="z-raised absolute top-3 left-3"><Link href={targetUrl} className="cursor-pointer" draggable={false}><div className="caption flex items-center gap-2 rounded-full bg-[#00000099] px-2 text-white"><span className="bg-vive-live size-2 rounded-full" />Live</div></Link></div>;
    $[10] = isClient;
    $[11] = isLive;
    $[12] = targetUrl;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== preferredDevices) {
    t7 = preferredDevices || [];
    $[14] = preferredDevices;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <div className="z-raised absolute bottom-2 left-2"><DeviceIcons deviceType={t7} iconContainerClassName="rounded-full border border-[#FFFFFF26] size-5 flex items-center justify-center bg-[#212A39]/50" containerClassName="gap-2" /></div>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== vodDuration) {
    t9 = vodDuration !== undefined && <div className="bg-vive-surface-normal/70 text-vive-primary-30 z-floating pointer-events-none absolute right-2 bottom-2 rounded-full px-2 py-0.5 text-[12px] font-medium">{formatDuration(vodDuration)}</div>;
    $[18] = vodDuration;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="pointer-events-none absolute inset-0 z-1 opacity-0 transition-opacity duration-300 lg:group-hover:opacity-100" style={{
      boxShadow: "inset 0px 5px 20px 0px #131C2680, inset 0px -5px 20px 0px #131C26E5"
    }} />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isCardHovered || $[22] !== isDwellPlaying || $[23] !== vodId) {
    t11 = (isCardHovered || isDwellPlaying) && vodId && <div className="fade-in animate-in z-interactive absolute inset-0 duration-300"><HoverVideoPreview vodId={vodId} /></div>;
    $[21] = isCardHovered;
    $[22] = isDwellPlaying;
    $[23] = vodId;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = image || roomDefaultImage.src;
  let t13;
  if ($[25] !== imageClassName) {
    t13 = cn("transition-all duration-300 select-none lg:group-hover:scale-110 lg:group-hover:brightness-110", imageClassName);
    $[25] = imageClassName;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      objectFit: "cover"
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== imageAlt || $[29] !== t12 || $[30] !== t13) {
    t15 = <OptimizedImage src={t12} overrideSrcWidth={WORLD_CARD_ADVERTISED_SRC_WIDTH} className={t13} fill={true} sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw" alt={imageAlt} style={t14} draggable={false} />;
    $[28] = imageAlt;
    $[29] = t12;
    $[30] = t13;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== cardRef || $[33] !== t11 || $[34] !== t15) {
    t16 = <div ref={cardRef} className="relative size-full">{t11}{t15}</div>;
    $[32] = cardRef;
    $[33] = t11;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16 || $[37] !== targetUrl) {
    t17 = <Link href={targetUrl} className="block size-full cursor-pointer select-none" draggable={false} prefetch={false}>{t10}{t16}</Link>;
    $[36] = t16;
    $[37] = targetUrl;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t5 || $[41] !== t6 || $[42] !== t8 || $[43] !== t9) {
    t18 = <ViverseCard.Content className="group/card z-raised mb-2 flex aspect-[1.72] w-full items-center rounded-[25px] align-middle lg:mb-2">{t5}{t6}{t8}{t9}{t17}</ViverseCard.Content>;
    $[39] = t17;
    $[40] = t5;
    $[41] = t6;
    $[42] = t8;
    $[43] = t9;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}